// src/components/ClientList/BulkAddClientModal.js
import React, { useState, useRef } from 'react';
import css from './BulkAddClientModal.module.css';

const REQUIRED_HEADERS = ['first_name', 'last_name', 'email'];
const ALL_HEADERS = ['first_name', 'last_name', 'email', 'phone', 'date_of_birth', 'gender', 'notes'];
const TEMPLATE_CSV = 'first_name,last_name,email,phone,date_of_birth,gender,notes\nJane,Smith,jane@example.com,555-1234,1990-05-15,female,Wants to focus on strength\nMark,Jones,mark@example.com,,,,\n';

function parseCSV(text) {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return { headers: [], rows: [], error: 'CSV must have a header row and at least one data row.' };

  const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));

  const missingRequired = REQUIRED_HEADERS.filter(h => !headers.includes(h));
  if (missingRequired.length > 0) {
    return { headers, rows: [], error: `Missing required columns: ${missingRequired.join(', ')}` };
  }

  const rows = lines.slice(1).map((line, i) => {
    const values = parseCSVLine(line);
    const row = {};
    headers.forEach((h, idx) => { row[h] = (values[idx] || '').trim(); });

    const errors = [];
    if (!row.first_name) errors.push('first_name required');
    if (!row.last_name) errors.push('last_name required');
    if (!row.email) errors.push('email required');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email)) errors.push('invalid email');

    return { ...row, _row: i + 2, _errors: errors };
  }).filter(r => Object.values(r).some((v, i) => i < headers.length && v !== ''));

  return { headers, rows, error: null };
}

function parseCSVLine(line) {
  const values = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      inQuotes = !inQuotes;
    } else if (ch === ',' && !inQuotes) {
      values.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  values.push(current);
  return values;
}

const BulkAddClientModal = ({ isOpen, onClose, onBulkAdd }) => {
  const [step, setStep] = useState('upload'); // 'upload' | 'preview' | 'results'
  const [csvText, setCsvText] = useState('');
  const [parsed, setParsed] = useState(null);
  const [parseError, setParseError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      setCsvText(text);
      processCSV(text);
    };
    reader.readAsText(file);
  };

  const handleTextChange = (e) => {
    setCsvText(e.target.value);
  };

  const processCSV = (text) => {
    const result = parseCSV(text);
    if (result.error) {
      setParseError(result.error);
      setParsed(null);
    } else {
      setParseError(null);
      setParsed(result);
    }
  };

  const handlePreview = () => {
    processCSV(csvText);
    const result = parseCSV(csvText);
    if (!result.error && result.rows.length > 0) {
      setParsed(result);
      setStep('preview');
    }
  };

  const validRows = parsed?.rows.filter(r => r._errors.length === 0) || [];
  const invalidRows = parsed?.rows.filter(r => r._errors.length > 0) || [];

  const handleImport = async () => {
    setLoading(true);
    const clients = validRows.map(({ _row, _errors, ...client }) => client);
    const result = await onBulkAdd(clients);
    setResults(result);
    setStep('results');
    setLoading(false);
  };

  const handleReset = () => {
    setStep('upload');
    setCsvText('');
    setParsed(null);
    setParseError(null);
    setResults(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'gymnet_clients_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className={css.overlay} onClick={onClose}>
      <div className={css.modal} onClick={e => e.stopPropagation()}>
        <div className={css.header}>
          <div>
            <h2 className={css.title}>Bulk Import Clients</h2>
            {step === 'upload' && <p className={css.subtitle}>Upload a CSV file or paste CSV data below</p>}
            {step === 'preview' && <p className={css.subtitle}>{validRows.length} valid · {invalidRows.length} with errors</p>}
            {step === 'results' && <p className={css.subtitle}>Import complete</p>}
          </div>
          <button className={css.closeButton} onClick={onClose}>×</button>
        </div>

        <div className={css.body}>
          {step === 'upload' && (
            <>
              <div className={css.templateRow}>
                <span className={css.helpText}>Required columns: <strong>first_name, last_name, email</strong>. Optional: phone, date_of_birth, gender, notes.</span>
                <button className={css.btnTemplate} onClick={downloadTemplate}>Download template</button>
              </div>

              <div className={css.uploadArea} onClick={() => fileInputRef.current?.click()}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleFileChange}
                  className={css.fileInput}
                />
                <div className={css.uploadIcon}>📂</div>
                <p className={css.uploadText}>Click to upload a CSV file</p>
              </div>

              <p className={css.orDivider}>— or paste CSV —</p>

              <textarea
                className={css.textarea}
                value={csvText}
                onChange={handleTextChange}
                placeholder={`first_name,last_name,email\nJane,Smith,jane@example.com`}
                rows={8}
                spellCheck={false}
              />

              {parseError && <p className={css.errorText}>{parseError}</p>}

              {parsed && !parseError && (
                <p className={css.successText}>
                  {parsed.rows.length} rows parsed — {parsed.rows.filter(r => r._errors.length === 0).length} valid
                </p>
              )}
            </>
          )}

          {step === 'preview' && parsed && (
            <>
              {invalidRows.length > 0 && (
                <div className={css.warningBanner}>
                  {invalidRows.length} row{invalidRows.length !== 1 ? 's' : ''} with errors will be skipped. Fix your CSV and re-upload to include them.
                </div>
              )}
              <div className={css.tableWrapper}>
                <table className={css.table}>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>First Name</th>
                      <th>Last Name</th>
                      <th>Email</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.rows.map((row) => (
                      <tr key={row._row} className={row._errors.length > 0 ? css.rowError : ''}>
                        <td className={css.rowNum}>{row._row}</td>
                        <td>{row.first_name || <span className={css.missing}>missing</span>}</td>
                        <td>{row.last_name || <span className={css.missing}>missing</span>}</td>
                        <td>{row.email || <span className={css.missing}>missing</span>}</td>
                        <td>
                          {row._errors.length > 0
                            ? <span className={css.badgeError}>{row._errors.join(', ')}</span>
                            : <span className={css.badgeOk}>Ready</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {step === 'results' && results && (
            <div className={css.results}>
              <div className={css.resultStat}>
                <span className={css.resultNum}>{results.created}</span>
                <span className={css.resultLabel}>Clients added</span>
              </div>
              <div className={css.resultStat}>
                <span className={css.resultNumMuted}>{results.skipped}</span>
                <span className={css.resultLabel}>Skipped (already exist)</span>
              </div>
              {results.errors?.length > 0 && (
                <div className={css.resultErrors}>
                  <p className={css.resultErrorTitle}>Errors:</p>
                  {results.errors.map((e, i) => <p key={i} className={css.resultErrorItem}>{e}</p>)}
                </div>
              )}
            </div>
          )}
        </div>

        <div className={css.actions}>
          {step === 'upload' && (
            <>
              <button className={css.btnSecondary} onClick={onClose}>Cancel</button>
              <button
                className={css.btnPrimary}
                onClick={handlePreview}
                disabled={!csvText.trim() || !!parseError || !parsed || parsed.rows.length === 0}
              >
                Preview Import
              </button>
            </>
          )}
          {step === 'preview' && (
            <>
              <button className={css.btnSecondary} onClick={() => setStep('upload')}>Back</button>
              <button
                className={css.btnPrimary}
                onClick={handleImport}
                disabled={loading || validRows.length === 0}
              >
                {loading ? 'Importing...' : `Import ${validRows.length} Client${validRows.length !== 1 ? 's' : ''}`}
              </button>
            </>
          )}
          {step === 'results' && (
            <>
              <button className={css.btnSecondary} onClick={handleReset}>Import More</button>
              <button className={css.btnPrimary} onClick={onClose}>Done</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkAddClientModal;
